// The app's gallery: every card of the public showcase (https://neurosimulator.ai), with the scene it
// belongs to, how much of it the app has, and the links that open it. The manifest is written by
// scripts/gallery.py from the showcase's own files (its public copy and Methods lines, its encoded
// media) and ba-web's audit (scripts/gallery-links.json); the media play from the gallery's own files.
// It's loaded when the sheet first opens, so the app's bundle doesn't carry it.

import type { ScenarioId } from '../scenarios/index.ts';

/** How much of a card the app has: `app`, the exact state opens; `partly`, the scene has part of it;
 *  `render`, a film or still with no interactive counterpart yet; `figure`, a result or dataset figure. */
export type GalleryState = 'app' | 'partly' | 'render' | 'figure';

/** The six provenance words (AGENTS.md; the lead's ruling of 2026-10-04, #2 5985263849): `predicted` is a statistical or
 *  machine-learned model's own output (an outcome prediction, a brain age), `simulated` a mechanistic model's, `derived`
 *  a processed measurement of the same thing. `showcase/build.py` holds the same tuple (`test/gallery-kinds.test.ts`). */
export type Provenance = 'simulated' | 'measured' | 'derived' | 'reference' | 'predicted' | 'artist-made';

export interface GalleryLink {
  label: string;
  /** The query src/app/url.ts reads, from its `?`. */
  q: string;
}

export interface GalleryMedia {
  kind: 'video' | 'image';
  /** The inline file: a 720p clip, or the 800 px still. */
  src: string;
  /** The tap-to-open file: 1080p, or the 1600 px still. */
  hd: string;
  /** A clip's poster. */
  poster?: string;
  width?: number | null;
  height?: number | null;
}

export interface GalleryItem {
  id: string;
  /** The showcase section (hero, reel, powers, flow, seizure, neurons, drugs, app, research, data). */
  section: string;
  scene: ScenarioId | null;
  state: GalleryState;
  kind: Provenance;
  title: string;
  line: string;
  /** The gallery's public Methods line: model and data. */
  methods: string;
  media: GalleryMedia;
  links: GalleryLink[];
  /** `contain` for a figure with its own background. */
  fit?: 'contain' | 'cover';
  /** A research card's verdict: Kept, Killed, Won, Measured. */
  tag?: string;
  date?: string;
  /** A clip that plays once (the reel); clips loop otherwise. */
  loop?: boolean;
}

export interface Gallery {
  about: string;
  showcase: { generated: string | null; hero_variant: string };
  mediaBase: string;
  sections: Record<string, string>;
  items: GalleryItem[];
}

let loading: Promise<Gallery> | null = null;
let loaded: Gallery | null = null;

/** The manifest, read once. */
export function loadGallery(): Promise<Gallery> {
  loading ??= import('./gallery.json').then((m) => {
    loaded = (m.default ?? m) as unknown as Gallery;
    return loaded;
  });
  return loading;
}

/** The manifest if it has been read (a tour's keys need it at once), else null. */
export const galleryIfLoaded = (): Gallery | null => loaded;

export const mediaUrl = (g: Pick<Gallery, 'mediaBase'>, file: string) => `${g.mediaBase}${file}`;

export const STATE_LABEL: Record<GalleryState, string> = {
  app: 'In the app',
  partly: 'Partly in the app',
  render: 'Film or still',
  figure: 'Figure',
};

export const KIND_LABEL: Record<Provenance, string> = {
  simulated: 'Simulated',
  measured: 'Measured',
  derived: 'Derived',
  reference: 'Reference',
  predicted: 'Predicted',
  // On screen `artist-made` reads "illustrated", as on the gallery page (ruling 2).
  'artist-made': 'Illustrated',
};

/** The cards for a scene, in the gallery's order; `null` lists research and data figures with no scene. */
export function itemsForScene(g: Gallery, scene: ScenarioId | null): GalleryItem[] {
  return g.items.filter((it) => it.scene === scene);
}

/** A plain-text search over a card's title, line, Methods and id. */
export function matches(it: GalleryItem, query: string): boolean {
  const q = query.trim().toLowerCase();
  if (!q) return true;
  return [it.title, it.line, it.methods, it.id, it.tag ?? ''].some((s) => s.toLowerCase().includes(q));
}

/** How well a card matches, for the palette's order among the cards that match: its title (2) before its line (1) before
 *  its Methods, id or tag (0); showcase order within a rank. Showcase v2.99's reel card named "20,000 tracers" in its
 *  Methods and, newest first, came before the card so titled. */
export function matchRank(it: GalleryItem, query: string): number {
  const q = query.trim().toLowerCase();
  if (!q) return 0;
  if (it.title.toLowerCase().includes(q)) return 2;
  if (it.line.toLowerCase().includes(q)) return 1;
  return 0;
}

/** The cards a query finds, best match first, showcase order within a rank. */
export function findCards(g: Gallery, query: string, limit = 6): GalleryItem[] {
  return g.items
    .filter((it) => matches(it, query))
    .sort((a, b) => matchRank(b, query) - matchRank(a, query))
    .slice(0, limit);
}
